// Step 3: Payment Processing (Authenticated Users Only)
// Security: User must be authenticated to process payment
// UX: Clear payment flow with Stripe Checkout integration

import React, { useState, useEffect } from 'react';
import { SubscriptionTier } from '../../config/subscriptions';

interface PaymentProcessingStepProps {
  user: any;
  selectedTier: SubscriptionTier;
  onPaymentSuccess: (sessionId: string) => void;
  onPaymentError: (error: string) => void;
  onBack: () => void;
}

const PaymentProcessingStep: React.FC<PaymentProcessingStepProps> = ({
  user,
  selectedTier,
  onPaymentSuccess,
  onPaymentError,
  onBack
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store user intent in their profile before payment
  useEffect(() => {
    const storePaymentIntent = async () => {
      try {
        // This would update the user's subscription_intent field
        console.log('📝 Storing payment intent for user:', user.id, selectedTier.id);
        // In a real implementation, you'd call Supabase to update the profile
      } catch (err) {
        console.error('Failed to store payment intent:', err);
      }
    };

    if (user && selectedTier) {
      storePaymentIntent();
    }
  }, [user, selectedTier]);

  const handleCheckoutRedirect = async () => {
    if (!user?.id) {
      onPaymentError('User authentication required. Please refresh and try again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const checkoutUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8788' 
        : 'https://eightythreelabs-subscription-checkout.yahya-darman-work.workers.dev';

      console.log('💳 Creating checkout session for authenticated user:', user.id);

      const response = await fetch(`${checkoutUrl}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tier: selectedTier.id,
          customer_email: user.email,
          user_id: user.id, // CRITICAL: Include user ID for security
          success_url: `${window.location.origin}/signup-success?session_id={CHECKOUT_SESSION_ID}&user_id=${user.id}`,
          cancel_url: `${window.location.origin}/signup-new?step=plan_selection&user_id=${user.id}`
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('✅ Checkout session created:', data.session_id);

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('❌ Checkout session creation failed:', err);
      let errorMessage = 'Failed to initiate payment. Please try again.';
      
      if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMessage = 'Unable to connect to payment services. Please check your internet connection or disable ad blockers.';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Complete Your Payment
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Secure checkout powered by Stripe
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Order Summary</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {selectedTier.name} Plan
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Annual subscription
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900 dark:text-white">
                  {selectedTier.priceDisplay}/year
                </div>
              </div>
            </div>
            
            {/* User Details */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Account:</span>
                <span className="text-gray-900 dark:text-white font-medium">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-600 dark:text-gray-400">Status:</span>
                <span className="text-blue-600 dark:text-blue-400">Ready for Payment</span>
              </div>
            </div>

            {/* Total */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
              <div className="flex items-center justify-between text-lg font-semibold">
                <span className="text-gray-900 dark:text-white">Total:</span>
                <span className="text-gray-900 dark:text-white">{selectedTier.priceDisplay}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Security Notice */}
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.25-5.25a12 12 0 00-16.5 0m16.5 0a12 12 0 01-16.5 0M12 3v18" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                Secure Payment Processing
              </h4>
              <div className="text-sm text-green-700 dark:text-green-300 mt-1 space-y-1">
                <p>• Your account is already created and verified</p>
                <p>• Payment processed securely through Stripe</p>
                <p>• Instant activation after payment confirmation</p>
                <p>• You can enter promotion codes during checkout</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-4">
          <button
            onClick={handleCheckoutRedirect}
            disabled={loading}
            className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors ${
              loading
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/20'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Redirecting to Stripe...
              </span>
            ) : (
              <>
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Continue to Secure Checkout
              </>
            )}
          </button>

          <button
            onClick={onBack}
            disabled={loading}
            className="w-full py-3 px-4 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
          >
            ← Back to Plan Selection
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 space-x-4">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2">✓</div>
              <span>Account</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2">✓</div>
              <span>Plan</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2">3</div>
              <span className="font-medium text-blue-600 dark:text-blue-400">Payment</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PaymentProcessingStep;